import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { PDFViewer } from '@/components/installations/pdf-viewer';
import { ChevronLeft, Download, FileText } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

interface DocumentInfo {
  id: number;
  filename: string;
  originalFilename: string;
  url: string;
  type: string;
  size: number;
  category?: string;
  createdAt?: string;
}

export default function DocumentViewerPage() {
  const [, setLocation] = useLocation();
  const params = useParams<{ id: string }>();
  const documentId = params?.id;
  const [error, setError] = useState<string | null>(null);

  // Query to fetch document info and content
  const {
    data: document,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['document', documentId],
    queryFn: async () => {
      if (!documentId) throw new Error('No document ID provided');

      try {
        const response = await fetch(`/api/documents/${documentId}`);

        if (!response.ok) {
          throw new Error(`Failed to load document (status ${response.status})`);
        }

        // Get the content type to determine how to process the response
        const contentType = response.headers.get('content-type') || '';

        if (contentType.includes('application/json')) {
          // This is document metadata (info about the document)
          const data = await response.json();
          return {
            ...data,
            url: `/api/documents/${documentId}`,
            isDirectContent: false,
          };
        } else {
          // This is direct document content (like a PDF file)
          // Create a blob URL for the content
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);

          // Extract filename from content-disposition header if available
          let filename = `document-${documentId}.pdf`;
          const disposition = response.headers.get('content-disposition');
          if (disposition && disposition.includes('filename=')) {
            const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1];
            }
          }

          return {
            id: Number(documentId),
            filename,
            originalFilename: filename,
            url,
            type: contentType,
            size: blob.size,
            isDirectContent: true,
          };
        }
      } catch (err) {
        console.error('Error fetching document:', err);
        setError(err instanceof Error ? err.message : 'Failed to load document');
        throw err;
      }
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Clean up blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (document?.isDirectContent && document.url) {
        URL.revokeObjectURL(document.url);
      }
    };
  }, [document]);

  // Handle download document function
  const handleDownload = () => {
    if (!document) return;

    const a = window.document.createElement('a');
    a.href = document.url;
    a.download = document.originalFilename || document.filename || `document-${documentId}.pdf`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  return (
    <div className="flex flex-col h-screen bg-white dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b p-4 flex items-center justify-between">
        <div className="flex items-center">
          <Link href="/">
            <Button variant="ghost" size="sm" className="mr-4">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </Link>

          <h1 className="text-lg font-medium flex items-center">
            <FileText className="h-5 w-5 mr-2 text-primary" />
            {isLoading
              ? 'Loading document...'
              : document
                ? document.originalFilename || document.filename
                : 'Document Viewer'}
          </h1>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          disabled={isLoading || isError || !document}
        >
          <Download className="h-4 w-4 mr-2" />
          Download
        </Button>
      </header>

      {/* Main content area */}
      <main className="flex-1 overflow-hidden">
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <div className="text-red-500 mb-4">
              <p>{error || 'Failed to load the document'}</p>
              <p className="text-sm text-muted-foreground mt-2">
                The document might be unavailable or you may not have permission to view it.
              </p>
            </div>
            <Link href="/">
              <Button variant="outline">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </Link>
          </div>
        )}

        {document &&
          !isLoading &&
          !isError &&
          (document.type.includes('pdf') || document.filename?.toLowerCase().endsWith('.pdf') ? (
            <PDFViewer
              pdfData={document.url}
              fileName={document.originalFilename || document.filename}
            />
          ) : document.type.includes('image') ? (
            <div className="flex items-center justify-center h-full bg-slate-100 dark:bg-slate-800 p-4">
              <img
                src={document.url}
                alt={document.originalFilename || document.filename || 'Document'}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-4">
              <p>This document type cannot be previewed in the browser.</p>
              <Button onClick={handleDownload} variant="outline" className="mt-4">
                <Download className="h-4 w-4 mr-2" />
                Download to View
              </Button>
            </div>
          ))}
      </main>
    </div>
  );
}
